import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useShellToolbarCenter } from '../shell/ShellToolbarContext';
import { useShellLayout } from '../shell/useShellLayout';
import { showToast } from '../notifications/toast';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import { getOfficePreviewKind, type DocumentEditorKind } from './documentFileTypes';
import {
  buildDocumentEditorSearch,
  documentEditorRoute,
  documentHostSrc,
  type DocumentEditorMode
} from './documentEditorUrls';
import { isEmbeddedEditor, isMinimalEditorChrome } from '../editorShell/editorChrome';
import { syncThemeToFrame } from '../theme/themeManager';
import { useHtmlThemeMode } from '../theme/useHtmlThemeMode';
import { shareDocumentLink } from './documentViewerActions';
import { DocumentViewerChromeBar } from './DocumentViewerChromeBar';
import { DocumentEditorShell } from './DocumentEditorShell';
import { SpreadsheetEditorShell } from '../spreadsheetEditor/SpreadsheetEditorShell';
import { useMediaFileMetadata } from '../mediaViewer/mediaViewerMetadata';
import type { FileReviewPanelPlacement } from '@jarvis/platform-file-review';
import { fileAccessRestrictedHref } from '../storageAccess/fileAccessRestrictedUrls';
import {
  DOCUMENT_HOST_OPEN_TIMEOUT_MS,
  isDocumentHostOpenFailedMessage
} from '../storageAccess/documentHostOpenFailed';
import './DocumentEditorPage.css';

const BARE_HOST_SHARE_MESSAGES = new Set([
  'jarvis-richedit:request-share',
  'jarvis-spreadsheet:request-share'
]);

export interface DocumentEditorPageProps {
  mode: DocumentEditorMode;
  /** Fixed editor kind for the edit routes; for preview it is derived from the file extension. */
  kind?: DocumentEditorKind;
  filesLinkTo?: string;
  filesLinkLabel?: string;
  /** Discussion panel placement for spreadsheet editor (edit and preview modes). */
  spreadsheetDiscussionPlacement?: FileReviewPanelPlacement;
  /** Discussion panel placement for RichEdit editor (edit and preview modes). */
  documentDiscussionPlacement?: FileReviewPanelPlacement;
}

export function DocumentEditorPage({
  mode,
  kind,
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser',
  spreadsheetDiscussionPlacement,
  documentDiscussionPlacement
}: DocumentEditorPageProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isNarrow } = useShellLayout();
  const htmlTheme = useHtmlThemeMode();
  const stayInEditor = isEmbeddedEditor(params) || isMinimalEditorChrome(params);
  const compactForOffice = isNarrow && !stayInEditor;
  const redirectedFromEditRef = useRef(false);
  const bareHostIframeRef = useRef<HTMLIFrameElement | null>(null);
  /** Calendar and other pages may register shell toolbar slots; editors need the full header width. */
  useShellToolbarCenter(null);
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();

  const resolvedKind: DocumentEditorKind | null = useMemo(() => {
    const fromPath = path ? getOfficePreviewKind(path) : null;
    // Email/deep links may use document-editor for CSV; path extension wins over route kind.
    if (kind && fromPath && kind !== fromPath) return fromPath;
    if (kind) return kind;
    return fromPath;
  }, [kind, path]);

  const hasSource = Boolean(mountId && path && resolvedKind);
  const redirectEditToPreview = hasSource && mode === 'edit' && compactForOffice;
  const useSpreadsheetEditorShell = resolvedKind === 'spreadsheet' && mode === 'edit' && !compactForOffice;
  const useRichEditEditorShell = resolvedKind === 'richedit' && mode === 'edit' && !compactForOffice;
  const useSpreadsheetPreviewShell = resolvedKind === 'spreadsheet' && mode === 'preview' && !compactForOffice;
  const useRichEditPreviewShell = resolvedKind === 'richedit' && mode === 'preview' && !compactForOffice;

  const src = useMemo(
    () =>
      hasSource &&
      !useSpreadsheetEditorShell &&
      !useRichEditEditorShell &&
      !useSpreadsheetPreviewShell &&
      !useRichEditPreviewShell
        ? documentHostSrc(resolvedKind!, mode, mountId, path, htmlTheme, !compactForOffice)
        : '',
    [
      hasSource,
      useSpreadsheetEditorShell,
      useRichEditEditorShell,
      useSpreadsheetPreviewShell,
      useRichEditPreviewShell,
      resolvedKind,
      mode,
      mountId,
      path,
      htmlTheme,
      compactForOffice
    ]
  );

  const metadata = useMediaFileMetadata(mountId, path, hasSource && mode === 'preview');

  const title = path ? displayNameFromPath(path) : '';

  const pageTitle = useMemo(() => {
    if (!resolvedKind) return '';
    if (mode === 'preview') {
      return resolvedKind === 'spreadsheet' ? 'JARVIS Sheets Viewer' : 'JARVIS Document Viewer';
    }
    return resolvedKind === 'spreadsheet' ? 'JARVIS Sheets Editor' : 'JARVIS Document Editor';
  }, [resolvedKind, mode]);

  useEffect(() => {
    if (!pageTitle) return;
    const previousTitle = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [pageTitle]);

  useEffect(() => {
    if (
      !hasSource ||
      mode !== 'edit' ||
      !compactForOffice ||
      !resolvedKind ||
      redirectedFromEditRef.current
    ) {
      return;
    }
    redirectedFromEditRef.current = true;
    showToast('Showing preview — open on a larger screen to edit.', 'info', 4000);
    navigate(
      `${documentEditorRoute(resolvedKind, 'preview')}?${buildDocumentEditorSearch(mountId, path, params)}`,
      { replace: true }
    );
  }, [compactForOffice, hasSource, mode, mountId, navigate, params, path, resolvedKind]);

  // Bare iframe (narrow preview / no shell): share + open-failed from the host.
  useEffect(() => {
    if (!src || !resolvedKind) return;
    let hostReady = false;

    const onMessage = (event: MessageEvent) => {
      const iframe = bareHostIframeRef.current;
      if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;
      const data = event.data;
      if (isDocumentHostOpenFailedMessage(data)) {
        navigate(fileAccessRestrictedHref(), { replace: true });
        return;
      }
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      const type = (data as { type?: unknown }).type;
      if (type === 'jarvis-richedit:ready' || type === 'jarvis-spreadsheet:ready') {
        hostReady = true;
        return;
      }
      if (typeof type !== 'string' || !BARE_HOST_SHARE_MESSAGES.has(type)) return;
      void shareDocumentLink(resolvedKind, mode === 'preview' ? 'preview' : 'edit', mountId, path);
    };

    window.addEventListener('message', onMessage);
    const timer = window.setTimeout(() => {
      if (!hostReady) navigate(fileAccessRestrictedHref(), { replace: true });
    }, DOCUMENT_HOST_OPEN_TIMEOUT_MS);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
    };
  }, [src, mode, resolvedKind, mountId, path, navigate]);

  if (!hasSource) {
    return (
      <div className="document-editor-page document-editor-page--empty">
        <p className="page-sub">No document selected.</p>
        <p className="page-sub">
          Open a supported file from <Link to={filesLinkTo}>{filesLinkLabel}</Link>.
        </p>
      </div>
    );
  }

  if (redirectEditToPreview) {
    return (
      <div className="document-editor-page document-editor-page--empty">
        <p className="page-sub">Opening preview...</p>
      </div>
    );
  }

  if (useSpreadsheetPreviewShell) {
    return (
      <div className="ops-fill-page ops-fill-page--bare document-editor-page document-editor-page--active">
        <SpreadsheetEditorShell mode="preview" mountId={mountId} path={path} title={title} />
      </div>
    );
  }

  if (useRichEditPreviewShell) {
    return (
      <div className="ops-fill-page ops-fill-page--bare document-editor-page document-editor-page--active">
        <DocumentEditorShell mode="preview" mountId={mountId} path={path} title={title} />
      </div>
    );
  }

  if (useSpreadsheetEditorShell) {
    return (
      <div className="ops-fill-page ops-fill-page--bare document-editor-page document-editor-page--active">
        <SpreadsheetEditorShell
          mode="edit"
          mountId={mountId}
          path={path}
          title={title}
          discussionPlacement={spreadsheetDiscussionPlacement}
        />
      </div>
    );
  }

  if (useRichEditEditorShell) {
    return (
      <div className="ops-fill-page ops-fill-page--bare document-editor-page document-editor-page--active">
        <DocumentEditorShell
          mode="edit"
          mountId={mountId}
          path={path}
          title={title}
          discussionPlacement={documentDiscussionPlacement}
        />
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare document-editor-page document-editor-page--active">
      <div className="document-editor-page__header">
        <span className="document-editor-page__title" title={path}>
          {title}
        </span>
      </div>
      {mode === 'preview' ? (
        <DocumentViewerChromeBar
          kind={resolvedKind!}
          mountId={mountId}
          relativePath={path}
          metadata={metadata}
        />
      ) : null}
      <iframe
        ref={bareHostIframeRef}
        className="document-editor-page__frame"
        src={src}
        title={title}
        onLoad={(event) => syncThemeToFrame(event.currentTarget.contentWindow)}
      />
    </div>
  );
}
